/**
 * Partner branding in the raw HTML, for link previews.
 *
 * Chat apps (iMessage, Slack, WhatsApp) and crawlers read index.html without
 * running JavaScript, so the client-side overlay (`use-partner-branding.ts` +
 * `use-brand-head.ts`) never reaches them: a partner link would unfurl with the
 * Tagada icon and "CRM App". The Vercel Routing Middleware (`apps/crm-app/middleware.ts`)
 * looks up the request host's `partners.branding` and rewrites the static head
 * with this module before serving it.
 *
 * The middleware is bundled by Vercel apart from Vite and imports this file
 * with a relative path: keep it free of `@/` aliases, workspace packages, React
 * and `import.meta.env`.
 *
 * Same logic as `payment-stack/dashboard/src/lib/branding/partner-link-preview.ts`,
 * except for the empty-field fallbacks, which follow this app.
 */

/** Tagada-owned domains (and their subdomains): never looked up as a partner host. */
export const TAGADA_DOMAINS = ['tagada.io', 'tagada.dev', 'tagadapay.com', 'tagadapay.dev', 'tagadapay.io'];

export const isTagadaHost = (host: string): boolean =>
  TAGADA_DOMAINS.some((d) => host === d || host.endsWith('.' + d));

/** Lowercased hostname without the port or a trailing dot, from a `Host` header value. */
export const normalizeHost = (host: string): string =>
  host.trim().toLowerCase().replace(/:\d+$/, '').replace(/\.$/, '');

/** Hosts the middleware looks up: anything that is not Tagada, local, or a Vercel preview. */
export const isPartnerBrandingHost = (host: string): boolean =>
  !!host &&
  host !== 'localhost' &&
  host !== '127.0.0.1' &&
  !host.endsWith('.vercel.app') &&
  !isTagadaHost(host);

/**
 * Public branding endpoint, the one the client overlay calls on partner hosts
 * (`SHARED_API_ORIGIN` in `src/lib/config.ts`). Partner domains only point at
 * the production deployment, hence the fixed host.
 */
export const partnerBrandingUrl = (host: string): string =>
  `https://api.tagada.io/api/public/crm/partner-branding?host=${encodeURIComponent(host)}`;

/** Past this delay the page is served unchanged rather than holding the navigation. */
export const PARTNER_BRANDING_TIMEOUT_MS = 1000;

/** The branding fields a link preview uses, validated. */
export interface LinkPreviewBranding {
  appName?: string;
  appDescription?: string;
  /** Always an absolute `https:` URL. */
  faviconUrl?: string;
  /** Always a `#` hex color. */
  primaryColor?: string;
}

const text = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim() ? value.trim() : undefined;

const httpsUrl = (value: string | undefined): string | undefined => {
  if (!value) return undefined;
  try {
    const url = new URL(value);
    return url.protocol === 'https:' ? url.href : undefined;
  } catch {
    return undefined;
  }
};

const HEX_COLOR = /^#(?:[0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;

const hexColor = (value: string | undefined): string | undefined =>
  value && HEX_COLOR.test(value) ? value : undefined;

/**
 * Read the endpoint's `branding` value. Null when the host has no branding
 * (`{"branding": null}`). The values are partner-controlled: a favicon that is
 * not an `https:` URL or a color that is not hex is dropped, as if unset.
 * Accepts the legacy `favicon` / `color` keys like `Partner.parsePartnerBranding`.
 */
export const readLinkPreviewBranding = (raw: unknown): LinkPreviewBranding | null => {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const r = raw as Record<string, unknown>;

  const out: LinkPreviewBranding = {};
  const appName = text(r.appName);
  const appDescription = text(r.appDescription);
  const faviconUrl = httpsUrl(text(r.faviconUrl) ?? text(r.favicon));
  const primaryColor = hexColor(text(r.primaryColor) ?? text(r.color));
  if (appName) out.appName = appName;
  if (appDescription) out.appDescription = appDescription;
  if (faviconUrl) out.faviconUrl = faviconUrl;
  if (primaryColor) out.primaryColor = primaryColor;
  return out;
};

export const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

/**
 * Tagada's icon files at the root (public/), plus the paths iOS and chat apps
 * probe whatever the page links to (`/favicon.ico`, sized or precomposed
 * touch icons). On a partner host the middleware redirects them to the
 * partner favicon; a partner with none keeps Tagada's, like the logo.
 */
export const isTagadaIconPath = (pathname: string): boolean =>
  /^\/(?:favicon(?:-(?:16|32))?\.(?:png|ico)|apple-touch-icon(?:-\d+x\d+)?(?:-precomposed)?\.png)$/.test(
    pathname,
  );

/** Tagada's icon links in index.html (files in public/), with their indentation and line break. */
const TAGADA_ICON_LINK =
  /[ \t]*<link\b[^>]*\shref="\/(?:favicon-16|favicon-32|favicon|apple-touch-icon)\.png"[^>]*>(?:[ \t]*\r?\n)?/g;

/**
 * Point every Tagada icon link at the partner favicon, keeping its `rel` but
 * dropping `type` / `sizes`, which describe Tagada's files. Without a partner
 * favicon they stay as they are: Tagada's icon is the fallback, like the logo.
 */
const replaceIconLinks = (html: string, faviconUrl: string | undefined): string =>
  html.replace(TAGADA_ICON_LINK, (tag) => {
    if (!faviconUrl) return tag;
    const indent = /^[ \t]*/.exec(tag)?.[0] ?? '';
    const rel = /\srel="([^"]*)"/.exec(tag)?.[1] ?? 'icon';
    const newline = /\r?\n$/.exec(tag)?.[0] ?? '';
    return `${indent}<link rel="${rel}" href="${escapeHtml(faviconUrl)}" />${newline}`;
  });

/** Replace the whole `<meta {attr}="{key}">` tag. Replacer functions keep `$&` & co. literal. */
const setMeta = (html: string, attr: 'name' | 'property', key: string, content: string): string =>
  html.replace(
    new RegExp(`<meta\\b[^>]*\\s${attr}="${key}"[^>]*>`),
    () => `<meta ${attr}="${key}" content="${escapeHtml(content)}" />`,
  );

/**
 * Rewrite the static index.html head with a partner's branding: icons, title,
 * `og:title`, description, `og:description` and `theme-color`. Every inserted
 * value is HTML-escaped. A field the partner left empty keeps the shell's
 * neutral value ("CRM App", empty description), as `mergePartnerBranding` keeps
 * the neutral base brand's on the client.
 */
export const rewritePartnerHtml = (html: string, branding: LinkPreviewBranding): string => {
  const { appName, appDescription, faviconUrl, primaryColor } = branding;

  let out = replaceIconLinks(html, faviconUrl);
  if (appName) {
    out = out.replace(/<title>[^<]*<\/title>/, () => `<title>${escapeHtml(appName)}</title>`);
    out = setMeta(out, 'property', 'og:title', appName);
  }
  if (appDescription) {
    out = setMeta(out, 'name', 'description', appDescription);
    out = setMeta(out, 'property', 'og:description', appDescription);
  }
  if (primaryColor) out = setMeta(out, 'name', 'theme-color', primaryColor);
  return out;
};
